import { Grid } from '@methanesat/ui-components';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addOneMapFeatureHighlight, resetOgiNearby, setActiveCapture } from '../../../../reducers';
import type { RootState } from '../../../../store';
import {
    DrawerWithCardsProps,
    EmissionDrawerCardData,
    isTotalOnlyInfo,
    PageIds,
    StacDateBlock
} from '../../../../types';
import { analytics, createHighlightPayload, formatDateForUrl, getActiveCaptureParams } from '../../../../utils';
import { useTranslate } from '../../../../hooks';
import { DrawerWithCards } from './DrawerWithCards';
import { DrawerCard } from './DrawerCard';
import { EmissionDrawerCardContent } from './EmissionDrawerCardContent';

export interface EmissionsDrawerWithCardsProps extends Omit<DrawerWithCardsProps, 'children'> {
    cardData: EmissionDrawerCardData[];
    expandedAccordion: string | null;
    onAccordionChange: (newExpandedAccordion: string, cardIndex: number) => void;
    handleDateChange: (day: StacDateBlock, targetId: string) => void;
    page?: PageIds;
}

/**
 * Wrapper around `DrawerWithCards` for displaying distinct point source and dispersed source cards.
 * Provides highlight and selection logic, and renders a list of emission summary cards.
 */
export const EmissionDrawerWithCards = ({
    cardData,
    onClose,
    open,
    expandedAccordion,
    activeCardIndex,
    onAccordionChange,
    snapPoint,
    setSnapPoint,
    handleDateChange,
    page
}: EmissionsDrawerWithCardsProps) => {
    const dispatch = useDispatch();
    const t = useTranslate();
    const ogiNearbyEnabled = useSelector((state: RootState) => state.pages.emissions.layers.ogiLayers.ogiNearbyEnabled);

    // fires an analytics event whenever the active card changes
    useEffect(() => {
        analytics.openEmissionsDrawerCard({
            targetId: cardData[activeCardIndex].targetId,
            // human-readable date format
            date: formatDateForUrl(new Date(cardData[activeCardIndex].startDateTime)),
            // lists all unique item ids in the card
            itemIds: [
                ...cardData[activeCardIndex].dispersedSourceEmissions,
                ...cardData[activeCardIndex].distinctPointEmissions
            ]
                .map((emission) => {
                    return emission.object.id;
                })
                .reduce((acc: string[], curr: string) => {
                    if (!acc.includes(curr)) acc.push(curr);
                    return acc;
                }, [])
        });
    }, [activeCardIndex, cardData]);

    // allows turning off the link-copying button in drawers with only total-only data
    const includesCardsWithData = useMemo(() => {
        for (let i = 0, l = cardData.length; i < l; i++) {
            if (cardData[i].distinctPointEmissions.length) {
                return true;
            }
            if (cardData[i].dispersedSourceEmissions.some((dispersedData) => !isTotalOnlyInfo(dispersedData)))
                return true;
        }
        return false;
    }, [cardData]);

    return (
        <DrawerWithCards
            open={open}
            onClose={onClose}
            title={
                cardData.length === 1
                    ? t('emissionsMapPage.drawerWithCards.emissions.titleSingular', {
                          count: cardData.length
                      })
                    : t('emissionsMapPage.drawerWithCards.emissions.titlePlural', {
                          count: cardData.length
                      })
            }
            activeCardIndex={activeCardIndex}
            snapPoint={snapPoint}
            setSnapPoint={setSnapPoint}
            shareable={includesCardsWithData}
        >
            <Grid container rowSpacing={3}>
                {cardData.map(
                    (
                        {
                            targetId,
                            targetGroupId,
                            itemId,
                            startDateTime,
                            distinctPointEmissions,
                            dispersedSourceEmissions,
                            dataUnavailable
                        },
                        idx
                    ) => (
                        <Grid size={{ xs: 12 }} key={targetId}>
                            <DrawerCard
                                data-active={activeCardIndex === idx ? '' : undefined}
                                data-testid="drawer-info-card"
                                elevation={activeCardIndex === idx ? 0 : 8}
                            >
                                <EmissionDrawerCardContent
                                    targetGroupId={targetGroupId}
                                    targetId={targetId}
                                    startDateTime={startDateTime}
                                    distinctPointEmissions={distinctPointEmissions}
                                    dispersedSourceEmissions={dispersedSourceEmissions}
                                    dataUnavailable={dataUnavailable}
                                    expandedId={expandedAccordion}
                                    isActive={idx === activeCardIndex}
                                    handleAccordionChange={(accordionId, info) => {
                                        onAccordionChange(accordionId, idx);
                                        if (info) {
                                            dispatch(addOneMapFeatureHighlight(createHighlightPayload(info)));
                                            const activeCaptureParams = getActiveCaptureParams(info);
                                            dispatch(setActiveCapture(activeCaptureParams));
                                            ogiNearbyEnabled && dispatch(resetOgiNearby());
                                        }
                                        // Restore activeCapture when the user expands an unavailable card that still represents a valid capture.
                                        else if (itemId) dispatch(setActiveCapture({ itemId, targetId }));
                                    }}
                                    handleDateChange={handleDateChange}
                                    ogiNearbyEnabled={ogiNearbyEnabled}
                                    page={page}
                                />
                            </DrawerCard>
                        </Grid>
                    )
                )}
            </Grid>
        </DrawerWithCards>
    );
};
